import type { GSplatRevealShowcaseEffectOptions } from './GSplatRevealShowcaseEffect'
import { GSplatRevealShowcaseEffect } from './GSplatRevealShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const spreadShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform vec3 uCenter;

${SHOWCASE_SHADER_COMMON_GLSL}

float revealSpreadSourceTime() {
  return uTime * uTimeScale;
}

float revealSpreadTt(float t) {
  return t * t * 0.4 + 0.5;
}

float revealSpreadFactor(float tt) {
  return min(1.0, 0.3 + max(0.0, tt * 0.05));
}

vec3 revealSpreadToSourceSpace(vec3 local) {
  vec2 horizontal = local.xy;
  return vec3(horizontal.x, local.z, horizontal.y);
}

vec3 revealSpreadFromSourceSpace(vec3 local) {
  return vec3(local.x, local.z, local.y);
}

void modifySplatCenter(inout vec3 center) {
  vec3 local = center - uCenter;
  vec3 sourceLocal = revealSpreadToSourceSpace(local);
  float t = revealSpreadSourceTime();
  float tt = revealSpreadTt(t);

  sourceLocal.xz *= revealSpreadFactor(tt);
  center = uCenter + revealSpreadFromSourceSpace(sourceLocal);
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  vec3 sourceLocal = revealSpreadToSourceSpace(originalCenter - uCenter);
  float t = revealSpreadSourceTime();
  float tt = revealSpreadTt(t);
  float l = length(sourceLocal.xz);
  float fullScale = min(tt - 7.0 - l * 2.5, 1.0);
  float dotScale = min(tt - 1.0 - l * 2.0, 1.0) * 0.2;

  gsplatApplyUniformScale(covA, covB, max(max(fullScale, dotScale), 0.0));
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = center - uCenter;
  float t = revealSpreadSourceTime();
  float tt = revealSpreadTt(t);
  float spread = revealSpreadFactor(tt);
  vec3 sourceLocal = revealSpreadToSourceSpace(local);

  sourceLocal.xz /= max(spread, 0.0001);

  float l = length(sourceLocal.xz);
  float visible = showcaseSaturate(tt - l * 2.5 - 3.0);
  color = mix(vec4(0.3), color, visible);
}
`

const spreadShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn revealSpreadSourceTime() -> f32 {
  return uniform.uTime * uniform.uTimeScale;
}

fn revealSpreadTt(t: f32) -> f32 {
  return t * t * 0.4 + 0.5;
}

fn revealSpreadFactor(tt: f32) -> f32 {
  return min(1.0, 0.3 + max(0.0, tt * 0.05));
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = *center - uniform.uCenter;
  var sourceLocal = vec3f(local.x, local.z, local.y);
  let tt = revealSpreadTt(revealSpreadSourceTime());
  sourceLocal.xz = sourceLocal.xz * revealSpreadFactor(tt);
  *center = uniform.uCenter + vec3f(sourceLocal.x, sourceLocal.z, sourceLocal.y);
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  var sourceLocal = vec3f(
    originalCenter.x - uniform.uCenter.x,
    originalCenter.z - uniform.uCenter.z,
    originalCenter.y - uniform.uCenter.y
  );
  let tt = revealSpreadTt(revealSpreadSourceTime());
  let l = length(sourceLocal.xz);
  let fullScale = min(tt - 7.0 - l * 2.5, 1.0);
  let dotScale = min(tt - 1.0 - l * 2.0, 1.0) * 0.2;
  *scale = (*scale) * max(max(fullScale, dotScale), 0.0);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = center - uniform.uCenter;
  let t = revealSpreadSourceTime();
  let tt = revealSpreadTt(t);
  let spread = revealSpreadFactor(tt);
  let sourceLocal = vec3f(local.x, local.z, local.y) / max(spread, 0.0001);
  let visible = showcaseSaturate(tt - length(sourceLocal.xz) * 2.5 - 3.0);
  *color = mix(vec4f(0.3), *color, visible);
}
`

export class GSplatRevealSpreadEffect extends GSplatRevealShowcaseEffect {
  constructor(options: GSplatRevealShowcaseEffectOptions = {}) {
    super(
      {
        id: 'reveal-spread',
        shader: spreadShaderGLSL,
        shaderWGSL: spreadShaderWGSL,
      },
      options
    )
  }
}
